import httpService from '@/core/http-service'
import store from '@/store'
import _ from 'lodash'
import myMachineService from '@/core/my-machines-service'
import Bus from '@/core/message-bus'
import utils from '@/core/utils'

const state = {
  myMachines: null,
  myMachinesMetaData: null,
  vehicles: null,
  vehicleIdDictionary: null,
  machineOwnerDictionary: null,
  machineDictionary: null,
  currentMachine: null,
  currentMachineOwner: null,
  myMachinesFilter: '',
  globalMachineSelection: null,
  globalMachineOwnerSelection: null
}

const mutations = {
  set_my_machines(state, payload) {
    state.myMachines = payload
  },
  set_my_machines_meta_data(state, payload) {
    state.myMachinesMetaData = payload
  },
  set_vehicles(state, payload) {
    state.vehicles = payload
  },
  set_vehicle_id_dictionary(state, payload) {
    state.vehicleIdDictionary = payload
  },
  set_machine_owner_id_dictionary(state, payload) {
    state.machineOwnerDictionary = payload
  },
  set_machine_id_dictionary(state, payload) {
    state.machineDictionary = payload
  },
  set_current_machine(state, payload) {
    state.currentMachine = payload
  },
  set_current_machine_owner(state, payload) {
    state.currentMachineOwner = payload
  },
  set_my_machines_filter(state, payload) {
    state.myMachinesFilter = payload
  },
  set_global_machine_selection(state, payload) {
    state.globalMachineSelection = payload
  },
  set_global_machine_owner_selection(state, payload) {
    state.globalMachineOwnerSelection = payload
  }
}

const actions = {
  async getMyMachines({ commit, dispatch }) {
    if (store.getters['clientModel'].enableMyMachines) {
      const response = await httpService.get('my-machines')
      dispatch('createMachineOwnerDictionary', response.data.machineOwners)
      dispatch('createMachineDictionary', response.data)
      commit('set_my_machines', response.data)
    }
  },
  async getMyMachinesMetaData({ dispatch }) {
    if (store.getters['clientModel'].enableMyMachines) {
      const response = await httpService.get('my-machines/meta-data')
      dispatch('setMyMachinesMetaData', response.data)
    }
  },
  async setMyMachinesMetaData({ commit }, payload) {
    commit('set_my_machines_meta_data', payload)
  },
  async getVehicles({ commit, dispatch }) {
    if (store.getters['clientModel'].enableMyMachines) {
      const vehicles = (await httpService.get('my-machines/machine-models')).data
      dispatch('updateVehicles', vehicles)
    }
  },
  updateVehicles({ dispatch, commit }, payload) {
    dispatch('createVehicleIdDictionary', payload)
    commit('set_vehicles', payload)
  },
  createVehicleIdDictionary({ commit }, payload) {
    const types = _.reduce(payload, (result, obj) => {
      result[obj.id] = obj.name
      return result
    }, {})
    const brands = _.chain(payload).flatMap(v => v.brands).reduce((result, obj) => {
      result[obj.id] = obj.name
      return result
    }, {}).value()
    const models = _.chain(payload).flatMap(t => t.brands).flatMap(m => m.models).reduce((result, obj) => {
      result[obj.id] = obj.name
      return result
    }, {}).value()
    commit('set_vehicle_id_dictionary', _.merge(types, brands, models))
  },
  createMachineOwnerDictionary({ commit }, payload) {
    const dictionary = _.reduce(payload, (result, obj) => {
      result[obj.machineOwnerId] = obj.name
      return result
    }, {})
    commit('set_machine_owner_id_dictionary', dictionary)
  },
  createMachineDictionary({ commit }, payload) {
    const freeMachines = _.reduce(payload.machines, (result, obj) => {
      result[obj.machineId] = obj.name
      return result
    }, {})
    const ownedMachines = _.chain(payload.machineOwners).flatMap(owner => owner.machines).reduce((result, obj) => {
      result[obj.machineId] = obj.name
      return result
    }, {}).value()
    commit('set_machine_id_dictionary', { ...freeMachines, ...ownedMachines })
  },
  async addMachineOwner(_, payload) {
    const addedOrEditedMachineOwner = await httpService.put('my-machines/machine-owner', payload)
    return addedOrEditedMachineOwner.data
  },
  async addMachine(_, payload) {
    const addedOrEditedMachine = await httpService.put('my-machines/machine', payload)
    return addedOrEditedMachine.data
  },
  setCurrentMachine({ commit }, payload) {
    commit('set_current_machine', payload)
  },
  setCurrentMachineOwner({ commit }, payload) {
    commit('set_current_machine_owner', payload)
  },
  addMachineImages(_, payload) {
    const machineId = payload.machineId
    const image = payload.image
    const type = payload.type
    const formData = new FormData()
    formData.append('image', image, image.name)
    formData.append('description', '')
    formData.append('type', type)
    const headers = { 'Content-Type': 'multipart/form-data' }
    return httpService.put(`my-machines/machine/image/${machineId}`, formData, {}, headers)
  },
  deleteMachineImages(_, payload) {
    const imageId = payload.imageId
    return httpService.delete(`my-machines/machine/image/${imageId}`)
  },
  deleteMachine(_, payload) {
    return httpService.delete(`my-machines/machine/${payload.machineId}`)
  },
  deleteMachineOwner(_, payload) {
    return httpService.delete(`my-machines/machine-owner/${payload.machineOwnerId}`)
  },
  setMyMachinesFilter({ commit }, payload) {
    commit('set_my_machines_filter', payload.filter)
  },
  async setMyMachinesConsentGdpr({ commit }) {
    const response = await httpService.post('my-machines/confirmConsentGdpr')
    commit('set_my_machines', response.data)
  },
  setGlobalMyMachinesSelection({ commit }, payload) {
    commit('set_global_machine_selection', payload.machineId)
    commit('set_global_machine_owner_selection', payload.machineOwnerId)
  },
  updateCurrentMachineOwner({ dispatch, getters }, payload) {
    if(getters.currentMachineOwner.machineOwnerId === payload.machineOwnerId) {
      dispatch('setCurrentMachineOwner', payload)
    }
  },
  updateMachinesForOwner({ getters, commit, dispatch }, payload) {
    if(getters.myMachines && payload.machineOwnerId) {
      const currentMyMachines = _.cloneDeep(getters.myMachines)
      const owner = _.find(currentMyMachines.machineOwners, owner => owner.machineOwnerId === payload.machineOwnerId)
      if(owner) {
        const existingMachine = _.find(owner.machines, machine => machine.machineId === payload.machineId)
        if(existingMachine) {
          _.merge(existingMachine, payload)
        } else {
          owner.machines.push(payload)
        }
        commit('set_my_machines', currentMyMachines)
        dispatch('updateCurrentMachineOwner', owner)
        return owner
      }
    }
  },
  removeMachineForOwner({ getters, commit, dispatch }, payload) {
    if(getters.myMachines && payload.machineOwnerId) {
      const currentMyMachines = _.cloneDeep(getters.myMachines)
      const owner = _.find(currentMyMachines.machineOwners, owner => owner.machineOwnerId === payload.machineOwnerId)
      if(owner) {
        const existingMachineIndex = _.findIndex(owner.machines, machine => machine.machineId === payload.machineId)
        if(existingMachineIndex !== -1) {
          owner.machines.splice(existingMachineIndex, 1)
        }
        commit('set_my_machines', currentMyMachines)
        dispatch('updateCurrentMachineOwner', owner)
        return owner
      }
    }
  },
  async updateCustomMachine({ dispatch }, payload) {
    const vehicles = (await httpService.put(`my-machines/machine-models/${payload.id}`, { name: payload.name })).data
    dispatch('updateVehicles', vehicles)
  },
  async deleteCustomMachine({ dispatch }, payload) {
    const vehicles = (await httpService.delete(`my-machines/machine-models/${payload.id}`)).data
    dispatch('updateVehicles', vehicles)
  },
  async updateCustomField({ dispatch }, payload) {
    const data = (await httpService.put(`my-machines/meta-data/machine-field-type/${payload.id}`, { name: payload.name })).data
    dispatch('setMyMachinesMetaData', data)
  },
  async deleteCustomField({ dispatch }, payload) {
    const data = (await httpService.delete(`my-machines/meta-data/machine-field-type/${payload.id}`)).data
    dispatch('setMyMachinesMetaData', data)
  },
  async updateCustomMachineOwner({ dispatch }, payload) {
    const data = (await httpService.post(`my-machines/meta-data/owner-type/${payload.id}`, { ownerType: payload.name })).data
    dispatch('setMyMachinesMetaData', data)
  },
  async deleteCustomMachineOwner({ dispatch }, payload) {
    const data = (await httpService.delete(`my-machines/meta-data/owner-type/${payload.id}`)).data
    dispatch('setMyMachinesMetaData', data)
  },
  async askToSetGlobalMachineSelection({ dispatch, getters }) {
    const hasComeFromMachine = utils.getQueryStringParam('hasComeFromMachine')
    if(hasComeFromMachine) {
      utils.removeQueryParam('hasComeFromMachine')
      const machine = _.find(getters.allMachines, machine => machine.machineId === +hasComeFromMachine)
      const sparePartsAlwaysSetMachine = JSON.parse(localStorage.getItem('sparePartsAlwaysSetMachine'))
      if(machine) {
        if(!_.isNil(sparePartsAlwaysSetMachine)) {
          if(sparePartsAlwaysSetMachine) {
            dispatch('setGlobalMyMachinesSelection', { machineId: machine.machineId, machineOwnerId: machine.machineOwnerId })
          }
        } else {
          Bus.publish('modal.show.select-machine', { machine: machine })
        }
      }
    }
  }
}

const getters = {
  myMachines: state => state.myMachines,
  hasMyMachines: state => state.myMachines && (state.myMachines.machines.length || state.myMachines.machineOwners.length),
  myMachinesMetaData: state => state.myMachinesMetaData,
  machineOwnerTypes: state => state.myMachinesMetaData ? _.map(state.myMachinesMetaData.customerTypes, (obj) => { return { value: obj.key, text: obj.value, custom: obj.custom } }) : [],
  machineCustomFieldOptions: state => state.myMachinesMetaData ? _.map(state.myMachinesMetaData.machineFields, (val, key) => { return { value: key, text: val } }) : [],
  metaDataMachineFields: state => state.myMachinesMetaData ? state.myMachinesMetaData.machineFields : {},
  vehicles: state => state.vehicles || [],
  customVehicles: state => {
    const allVehicles = _.cloneDeep(state.vehicles)
    const customVehicles = _.filter(allVehicles, v => {
      _.forEach(v.brands, b => {
        b.models = _.filter(b.models, model => model.customModel)
      })
      v.brands = _.filter(v.brands, b => b.customModel || b.models.length > 0)
      return v.brands.length > 0 || v.customModel
    })
    return customVehicles
  },
  vehicleIdDictionary: state => state.vehicleIdDictionary || {},
  machineOwnerDictionary: state => state.machineOwnerDictionary || {},
  machineDictionary: state => state.machineDictionary || {},
  machineOwnersOptions: state => state.myMachines ? _.map(state.myMachines.machineOwners, (owner) => { return { value: owner.machineOwnerId, text: owner.name } }) : [],
  currentMachine: state => state.currentMachine || {},
  currentMachineOwner: state => state.currentMachineOwner || {},
  hasCurrentMachine: state => !_.isEmpty(state.currentMachine),
  hasCurrentMachineOwner: state => !_.isEmpty(state.currentMachineOwner),
  myMachinesFilter: state => state.myMachinesFilter,
  myMachinesFilterSubstrings: state => _.chain(state.myMachinesFilter.toLowerCase()).split(' ').map(word => _.trim(word)).value(),
  filteredMachineOwners: (state, getters) => {
    if(getters.myMachines) {
      return myMachineService.filterMachineOwners(_.cloneDeep(getters.myMachines.machineOwners), getters.myMachinesFilter)
    } else {
      return []
    }
  },
  filteredMachines: (state, getters) => {
    if(getters.myMachines) {
      return myMachineService.filterMachines(_.cloneDeep(getters.myMachines.machines), getters.myMachinesFilter)
    } else {
      return []
    }
  },
  globalMachine(state) {
    return state.globalMachineSelection
  },
  globalMachineOwner(state) {
    return state.globalMachineOwnerSelection
  },
  hasGlobalSelection(state) {
    return state.globalMachineSelection || state.globalMachineOwnerSelection
  },
  readableGlobalSelection(state, getters) {
    return getters.hasGlobalSelection ? (`${getters.machineOwnerDictionary[getters.globalMachineOwner] || ''} - ${getters.machineDictionary[getters.globalMachine] || ''}`).replace(/^[\s-]+|[\s-]+$/g, '') : ''
  },
  allMachines(state) {
    return [..._.flatMap(state.myMachines.machineOwners, owner => owner.machines), ...state.myMachines.machines]
  },
  myMachinesIsLocked(state) {
    return state.myMachines && !state.myMachines.isGdprConsentGiven && state.myMachines.hasGdprSensitiveData
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
